import React, { useMemo, useState, useEffect } from "react";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Checkbox } from "@material-tailwind/react";

const DraggableRow = ({ row, index, moveRow }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: row.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="hover:bg-gray-50 dark:hover:bg-gray-800"
    >
      {row.cells.map((cell, i) => (
        <td
          key={i}
          className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap"
        >
          {cell.render("Cell")}
        </td>
      ))}
    </tr>
  );
};

const TableComponent = ({
  columns,
  data,
  withSearch = true,
  withCheckbox = true,
  withPagination = true,
  customActions,
  pageSize = 5,
  onRowSelect,
  onRowReorder,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState(data);

  useEffect(() => {
    if (!searchTerm) return setFilteredData(data);
    const lowered = searchTerm.toLowerCase();
    setFilteredData(
      data.filter((row) =>
        Object.values(row).some((val) =>
          String(val).toLowerCase().includes(lowered)
        )
      )
    );
  }, [searchTerm, data]);

  const defaultColumns = useMemo(() => {
    let baseCols = [
      {
        Header: "#",
        id: "rowIndex",
        Cell: ({ row }) => row.index + 1,
      },
      ...columns,
    ];

    if (withCheckbox) {
      baseCols.unshift({
        id: "selection",
        Header: ({ getToggleAllRowsSelectedProps }) => (
          <Checkbox {...getToggleAllRowsSelectedProps()} />
        ),
        Cell: ({ row }) => (
          <Checkbox {...row.getToggleRowSelectedProps()} color="blue" />
        ),
      });
    }

    if (customActions) {
      baseCols.push({
        Header: "Actions",
        Cell: ({ row }) => customActions(row.original),
      });
    }

    return baseCols;
  }, [columns, withCheckbox, customActions]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    setPageSize,
    gotoPage,
    pageCount,
    state: { pageIndex },
  } = useTable(
    {
      columns: defaultColumns,
      data: filteredData,
      initialState: { pageIndex: 0, pageSize },
    },
    useSortBy,
    usePagination,
    useRowSelect
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const newIndex = rows.findIndex((r) => r.id === over.id);

    const newOrder = arrayMove(filteredData, oldIndex, newIndex);
    setFilteredData(newOrder);
    if (onRowReorder) onRowReorder(newOrder);
  };

  useEffect(() => {
    if (onRowSelect) {
      const selected = rows
        .filter((row) => row.isSelected)
        .map((row) => row.original);
      onRowSelect(selected);
    }
  }, [rows]);

  return (
    <div className="w-full">
      {withSearch && (
        <div className="flex justify-between items-center mb-4">
          <input
            type="text"
            placeholder="Search..."
            className="border px-3 py-2 rounded-md w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      <div className="overflow-auto border rounded-md">
        <table
          {...getTableProps()}
          className="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
        >
          <thead className="bg-gray-50 sticky top-0 z-10 dark:bg-gray-900">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, i) => (
                  <th
                    key={i}
                    {...column.getHeaderProps(
                      column.getSortByToggleProps?.() || {}
                    )}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    {column.render("Header")}
                    {column.isSorted && (
                      <span>{column.isSortedDesc ? " 🔽" : " 🔼"}</span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={rows.map((row) => row.id)}
              strategy={verticalListSortingStrategy}
            >
              <tbody
                {...getTableBodyProps()}
                className="bg-white divide-y dark:bg-gray-800 dark:divide-gray-700"
              >
                {page.map((row, i) => {
                  prepareRow(row);
                  return (
                    <DraggableRow
                      key={row.id}
                      row={row}
                      index={i}
                      moveRow={() => {}}
                    />
                  );
                })}
              </tbody>
            </SortableContext>
          </DndContext>
        </table>
      </div>

      {withPagination && (
        <div className="mt-4 flex justify-between items-center">
          <span className="text-sm text-gray-500 dark:text-gray-300">
            Showing {page.length} of {filteredData.length}
          </span>
          <div className="flex space-x-2">
            {Array.from({ length: pageCount }, (_, i) => (
              <button
                key={i}
                onClick={() => gotoPage(i)}
                className={`px-3 py-1 rounded-md text-sm ${
                  pageIndex === i
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 dark:bg-gray-700 dark:text-white"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TableComponent;
